import { Component, computed, input } from '@angular/core';
import {
  DEFAULT_ADMIN_USER_PASSWORD_POLICY,
  resolveAdminUserPasswordRuleViews,
  type AdminUserPasswordPolicy,
} from '@nebula/shared-vault/data-access';

@Component({
  selector: 'app-admin-user-password-rules',
  standalone: true,
  template: `
    <div class="vault-password-rules" role="group" aria-label="Requisitos de la clave">
      <p class="vault-password-rules__title">Requisitos de la clave</p>
      <p class="vault-password-rules__hint">Política para crear o resetear clave.</p>
      <ul class="vault-password-rules__list">
        @for (rule of rules(); track rule.key) {
          <li
            class="vault-password-rules__item"
            [class.vault-password-rules__item--met]="rule.state === 'met'"
            [class.vault-password-rules__item--unmet]="rule.state === 'unmet'"
          >
            <span class="vault-password-rules__icon" aria-hidden="true">
              @if (rule.state === 'met') {
                ✓
              } @else if (rule.state === 'unmet') {
                ✗
              } @else {
                ○
              }
            </span>
            {{ rule.label }}
          </li>
        }
      </ul>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .vault-password-rules {
      margin: 0;
      padding: 0.75rem 1rem;
      border: 1px solid var(--md-sys-color-outline-variant, #dce3ee);
      border-radius: 8px;
      background: var(--md-sys-color-surface-container-low, #f0f4fa);
    }

    .vault-password-rules__title {
      margin: 0 0 0.25rem;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--md-sys-color-on-surface, var(--color-title, inherit));
    }

    .vault-password-rules__hint {
      margin: 0 0 0.5rem;
      font-size: 0.75rem;
      line-height: 1.35;
      color: var(--color-subtitle, var(--md-sys-color-on-surface-variant, #666));
    }

    .vault-password-rules__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }

    .vault-password-rules__item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8125rem;
      line-height: 1.4;
      color: var(--color-subtitle, var(--md-sys-color-on-surface-variant, #666));
    }

    .vault-password-rules__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      flex-shrink: 0;
      font-size: 0.75rem;
      font-weight: 700;
    }

    .vault-password-rules__item--met {
      color: #1b7f3d;
    }

    .vault-password-rules__item--met .vault-password-rules__icon {
      color: #1b7f3d;
    }

    .vault-password-rules__item--unmet {
      color: #b42318;
    }

    .vault-password-rules__item--unmet .vault-password-rules__icon {
      color: #b42318;
    }

    :host-context(.dark-theme) .vault-password-rules,
    .dark-theme :host .vault-password-rules {
      background: var(--color-bg-content-secondary, #252530);
      border-color: var(--color-border, #444746);
    }

    :host-context(.dark-theme) .vault-password-rules__title,
    .dark-theme :host .vault-password-rules__title {
      color: var(--color-title, #f0f0f0);
    }

    :host-context(.dark-theme) .vault-password-rules__hint,
    .dark-theme :host .vault-password-rules__hint {
      color: var(--color-subtitle, #b0b0b0);
    }

    :host-context(.dark-theme) .vault-password-rules__item,
    .dark-theme :host .vault-password-rules__item {
      color: var(--color-subtitle, #b0b0b0);
    }

    :host-context(.dark-theme) .vault-password-rules__item--met,
    .dark-theme :host .vault-password-rules__item--met {
      color: #6bcf8e;
    }

    :host-context(.dark-theme) .vault-password-rules__item--met .vault-password-rules__icon,
    .dark-theme :host .vault-password-rules__item--met .vault-password-rules__icon {
      color: #6bcf8e;
    }

    :host-context(.dark-theme) .vault-password-rules__item--unmet,
    .dark-theme :host .vault-password-rules__item--unmet {
      color: #f97066;
    }
  `,
})
export class AdminUserPasswordRulesComponent {
  readonly password = input('');
  readonly policy = input<AdminUserPasswordPolicy>(DEFAULT_ADMIN_USER_PASSWORD_POLICY);

  readonly rules = computed(() =>
    resolveAdminUserPasswordRuleViews(this.policy(), this.password()),
  );
}
